import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  TextField,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { KeyboardHide } from '@mui/icons-material';
import { XIcon } from '@phosphor-icons/react';
import { TMDbMovie } from '../../services/tmdbApi';
import CustomButton from '../widgets/CustomButton';
import { glassStyles } from '../../tokens/designTokens';
import BottomSheet, { BottomSheetDraggableHeader } from './BottomSheet';
import { font } from '../../utils/fontHelper';
import { useKeyboardAware } from '../../hooks/useKeyboardAware';
import { useReviewDraft } from '../../hooks/useReviewDraft';
import { dismissNativeKeyboard, useSuppressKeyboardDismissButton } from '../../contexts/NativeKeyboardContext';

const REVIEW_SOFT_LIMIT = 2000;
const REVIEW_HARD_LIMIT = REVIEW_SOFT_LIMIT + 50;

export interface WriteReviewDialogProps {
  open: boolean;
  onClose: () => void;
  initialReview: string;
  movie: TMDbMovie;
  userId: string;
  standardizedMovieId: string;
  /** Returns the review text to the parent (in-memory, not persisted as a draft). */
  onSave: (review: string) => void;
  /** Persisted draft was just saved (parent may want to refresh drafts list). */
  onDraftSaved?: () => void;
  /** Persisted draft was just discarded. */
  onDraftDiscarded?: () => void;
  /** When true, hides draft controls and shows a simple Cancel instead. */
  isEditMode?: boolean;
  /** Toast/haptic feedback hooks (optional — parent owns the UI). */
  onFeedback?: (
    kind: 'review-added' | 'draft-saved' | 'draft-discarded' | 'error',
    message?: string
  ) => void;
  /**
   * Current draft state for this movie (rating/visibility/date) — preserved
   * when explicitly saving a draft so the user doesn't lose those alongside
   * the review text.
   */
  draftContext?: {
    rating: number | null;
    visibility: 'anyone' | 'only-me';
    watchedDate: string; // ISO string
    watchedDatePrecision: 'day' | 'month' | 'year';
  };
  zIndex?: number;
}

const WriteReviewDialog: React.FC<WriteReviewDialogProps> = ({
  open,
  onClose,
  initialReview,
  movie,
  userId,
  standardizedMovieId,
  onSave,
  onDraftSaved,
  onDraftDiscarded,
  onFeedback,
  draftContext,
  isEditMode = false,
  zIndex,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isKeyboardOpen, keyboardHeight, scrollIntoViewOnFocus, scrollContainerRef, isNativePlatform } = useKeyboardAware();
  useSuppressKeyboardDismissButton(open);

  const draftHook = useReviewDraft(userId);

  const [localReview, setLocalReview] = useState(initialReview ?? '');
  const [charCount, setCharCount] = useState((initialReview ?? '').length);
  const reviewInputRef = useRef<HTMLTextAreaElement | null>(null);
  const initialReviewRef = useRef(initialReview);

  // Reset on open. Uncontrolled textarea — push initial value into the DOM.
  useEffect(() => {
    if (!open) return;
    setLocalReview(initialReview ?? '');
    setCharCount((initialReview ?? '').length);
    initialReviewRef.current = initialReview ?? '';
    if (reviewInputRef.current) reviewInputRef.current.value = initialReview ?? '';
  }, [open, initialReview]);

  const syncReviewFromInput = useCallback(() => {
    const el = reviewInputRef.current;
    if (!el) return;
    setLocalReview((prev) => (prev === el.value ? prev : el.value));
  }, []);

  // Word-boundary sync: keeps localReview in sync without re-rendering on every key.
  // charCount updates on every keystroke so the counter is always accurate.
  const handleReviewInput = useCallback((e: React.FormEvent<HTMLTextAreaElement>) => {
    const target = (e.target ?? e.currentTarget) as HTMLTextAreaElement;
    if (!target || target.value == null) return;
    setCharCount(target.value.length);
    const native = e.nativeEvent as InputEvent;
    const data = native.data;
    if (data == null || /[\s.,;:!?\-—)\]\}"']/.test(data)) {
      setLocalReview((prev) => (prev === target.value ? prev : target.value));
    }
  }, []);

  const getCurrentValue = (): string =>
    reviewInputRef.current?.value ?? localReview ?? '';

  const hasUnsavedChanges = (): boolean => {
    return getCurrentValue() !== initialReviewRef.current;
  };

  // Single Save action: commits text back to the parent dialog AND, when not
  // editing a published review, persists as a draft so the text survives close.
  const handleSave = async () => {
    const currentReview = getCurrentValue();
    onSave(currentReview);

    const canPersistDraft =
      !isEditMode &&
      currentReview.trim() &&
      standardizedMovieId &&
      standardizedMovieId !== 'unknown';

    if (canPersistDraft) {
      try {
        await draftHook.saveDraftImmediate(
          {
            movieId: standardizedMovieId,
            rating: draftContext?.rating ?? null,
            review: currentReview,
            visibility: draftContext?.visibility ?? 'anyone',
            watchedDate: draftContext?.watchedDate ?? new Date().toISOString(),
            watchedDatePrecision: draftContext?.watchedDatePrecision ?? 'day',
          },
          {
            title: movie.Title,
            poster: movie.Poster,
            year: movie.Year,
          }
        );
        onDraftSaved?.();
        onFeedback?.('draft-saved');
      } catch (err) {
        console.error('Failed to save draft:', err);
        onFeedback?.('error', "Couldn't save draft");
      }
    } else {
      onFeedback?.('review-added');
    }
    onClose();
  };

  const handleDiscard = async () => {
    if (standardizedMovieId && standardizedMovieId !== 'unknown') {
      try {
        await draftHook.deleteDraft(standardizedMovieId);
        onDraftDiscarded?.();
      } catch (err) {
        console.error('Failed to delete draft:', err);
      }
    }
    if (reviewInputRef.current) reviewInputRef.current.value = '';
    setLocalReview('');
    initialReviewRef.current = '';
    onSave('');
    onFeedback?.('draft-discarded');
    onClose();
  };

  const handleClose = () => {
    syncReviewFromInput();
    if (!hasUnsavedChanges()) {
      onClose();
      return;
    }
    // Save changes back to parent in-memory; explicit "Save draft" is a separate path.
    onSave(getCurrentValue());
    onClose();
  };

  const renderHeader = () => (
    <BottomSheetDraggableHeader sx={{
      px: 2,
      py: 1.5,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
    }}>
      <IconButton
        onClick={handleClose}
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' },
        }}
      >
        <XIcon size={20} color={theme.palette.text.primary} />
      </IconButton>
      <Typography sx={{
        fontWeight: 600,
        fontFamily: font(),
        color: theme.palette.text.primary,
        fontSize: 18,
        flex: 1,
        textAlign: 'center',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {movie.Title}
      </Typography>
      {/* Spacer to balance the close button and keep title centered */}
      <Box sx={{ width: 40, flexShrink: 0 }} />
    </BottomSheetDraggableHeader>
  );

  const renderBody = () => (
    <Box
      ref={scrollContainerRef as React.RefObject<HTMLDivElement>}
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-y',
        px: 2,
        pt: 2,
        pb: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <TextField
        multiline
        minRows={isMobile ? 12 : 8}
        defaultValue={initialReview ?? ''}
        onInput={handleReviewInput}
        onBlur={syncReviewFromInput}
        onFocus={scrollIntoViewOnFocus}
        inputRef={reviewInputRef}
        placeholder="Write about the movie here.."
        variant="outlined"
        fullWidth
        autoFocus
        inputMode="text"
        sx={{
          flex: 1,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            fontFamily: font(),
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(255, 255, 255, 0.4)',
            height: '100%',
            alignItems: 'flex-start',
            '& fieldset': { borderColor: 'transparent' },
            '&:hover fieldset': {
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.primary.main,
              borderWidth: '2px',
            },
          },
          '& .MuiInputBase-input': {
            fontFamily: font(),
            fontSize: 16,
            lineHeight: 1.5,
            color: theme.palette.text.primary,
            '&::placeholder': {
              color: theme.palette.text.secondary,
              opacity: 0.7,
            },
          },
        }}
        InputProps={{
          sx: {
            '& textarea': {
              resize: 'none',
              WebkitAppearance: 'none',
              fontSize: 16,
            },
          },
        }}
        inputProps={{
          enterKeyHint: 'enter',
          autoCapitalize: 'sentences',
          autoCorrect: 'on',
          spellCheck: true,
          autoComplete: 'off',
          maxLength: REVIEW_HARD_LIMIT,
        }}
      />
      {charCount >= 100 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
          <Typography sx={{
            fontSize: 11,
            fontFamily: font(),
            color: charCount > REVIEW_SOFT_LIMIT
              ? theme.palette.warning.main
              : theme.palette.text.secondary,
          }}>
            {charCount} / {REVIEW_SOFT_LIMIT}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderFooter = () => (
    <Box sx={{
      px: 2,
      pt: 1.5,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
      pb: isKeyboardOpen ? 1.5 : 'calc(var(--safe-area-inset-bottom) + 12px)',
      backdropFilter: 'blur(20px)',
      bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.5)',
    }}>
      <CustomButton
        semantic="tertiary"
        onClick={isEditMode ? handleClose : handleDiscard}
        sx={{ flex: 1, height: 44, fontSize: 14, fontWeight: 600, textTransform: 'none', borderRadius: 2.5 }}
      >
        {isEditMode ? 'Cancel' : 'Discard'}
      </CustomButton>
      <CustomButton
        semantic="brand"
        onClick={() => void handleSave()}
        sx={{ flex: 1, height: 44, fontSize: 14, fontWeight: 600, textTransform: 'none', borderRadius: 2.5 }}
      >
        Save
      </CustomButton>
      {isNativePlatform && isKeyboardOpen && (
        <IconButton
          onClick={() => void dismissNativeKeyboard()}
          sx={{
            width: 44,
            height: 44,
            flexShrink: 0,
            color: theme.palette.text.secondary,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
            borderRadius: 2,
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
            },
          }}
        >
          <KeyboardHide sx={{ fontSize: 22 }} />
        </IconButton>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <BottomSheet
        open={open}
        onClose={handleClose}
        height={isKeyboardOpen ? `calc(100dvh - ${keyboardHeight}px - env(safe-area-inset-top))` : 'calc(100dvh - env(safe-area-inset-top))'}
        bottom={isKeyboardOpen ? `${keyboardHeight}px` : 0}
        zIndex={zIndex || 1300}
        modalProps={{
          disableEnforceFocus: true,
          disableAutoFocus: true,
          disableRestoreFocus: true,
        }}
      >
        {renderHeader()}
        {renderBody()}
        {renderFooter()}
      </BottomSheet>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: zIndex || 1300 }}
      PaperProps={{
        sx: {
          maxHeight: '85dvh',
          display: 'flex',
          flexDirection: 'column',
          ...glassStyles.popup(theme.palette.mode === 'dark'),
        },
      }}
    >
      {renderHeader()}
      {renderBody()}
      {renderFooter()}
    </Dialog>
  );
};

export default WriteReviewDialog;
